if (!customElements.get('product-swiper-media')) {
    customElements.define(
        'product-swiper-media',
        class ProductSwiperMedia extends HTMLElement {
            productSwipper = undefined;
            viewerPreview = undefined;
            viewerRoot = undefined;
            viewerAssetsPromise = undefined;
            activeMediaIndex = -1;
            asyncImageFrame = undefined;
            deferredImageObservers = [];
            thumbScrollLockIndex = -1;
            thumbScrollLockTimer = undefined;

            constructor() {
                super();
                this.handleThumbClick = this.handleThumbClick.bind(this);
                this.handleDesktopScroll = this.handleDesktopScroll.bind(this);
                this.handleDesktopNextClick = this.handleDesktopNextClick.bind(this);
                this.handleViewerTrigger = this.handleViewerTrigger.bind(this);
                this.render();
            }

            render() {
                const isMobile = window.innerWidth < 750;
                const thumbsContentInner = this.querySelector('.thumbs-content_inner');

                if (!thumbsContentInner) return;

                this.destroySwiper();
                this.destroyViewer();
                this.teardownDesktopMode();
                this.teardownDeferredImageLoading();
                this.bindThumbClick(thumbsContentInner);
                this.activeMediaIndex = -1;
                this.thumbScrollLockIndex = -1;
                this.clearThumbScrollLockTimer();

                if (isMobile) {
                    this.initMobileSwiper();
                } else {
                    this.initDesktopMedia();
                }

                this.initViewer();
                this.initColorVariantThumb();
                this.initDeferredImageLoading();
            }

            // 初始化移动端轮播
            initMobileSwiper() {
                const productSwiper = this.querySelector('.product-swiper');

                if (!productSwiper) return;

                this.productSwipper = new Swiper(productSwiper, {
                    spaceBetween: 0,
                    slidesPerView: 1,
                    freeMode: false,
                    loop: true,
                    watchSlidesProgress: false,
                    navigation: {
                        nextEl: ".product-medias-swiper .swiper-button-next",
                        prevEl: ".product-medias-swiper .swiper-button-prev",
                    },
                    on: {
                        slideChange: () => {
                            const activeIndex = this.productSwipper?.realIndex || 0;
                            this.setActiveMediaIndex(activeIndex, false);
                        },
                        slideChangeTransitionEnd: () => {
                            this.playCurrentSlideVideo();
                        }
                    }
                });

                this.setActiveMediaIndex(this.productSwipper?.realIndex || 0, false);
                this.playCurrentSlideVideo();
            }

            // 初始化PC端纵向滚动
            initDesktopMedia() {
                const mediaScroller = this.querySelector('.product-medias-swiper');
                const swiperRoot = this.querySelector('.product-swiper');
                const swiperWrapper = this.querySelector('.product-swiper .swiper-wrapper');
                const slides = this.getSlides();

                if (!mediaScroller || !swiperRoot || !swiperWrapper || !slides.length) return;

                swiperRoot.style.removeProperty('transform');
                swiperRoot.style.removeProperty('height');
                swiperWrapper.style.removeProperty('transform');
                swiperWrapper.style.removeProperty('transition-duration');
                swiperWrapper.style.removeProperty('height');

                slides.forEach((slide, index) => {
                    slide.dataset.mediaIndex = index;
                    slide.style.removeProperty('width');
                    slide.style.removeProperty('height');
                    slide.style.removeProperty('margin-right');
                    slide.style.removeProperty('margin-left');
                    slide.style.removeProperty('transform');
                });

                mediaScroller.scrollTop = 0;
                mediaScroller.addEventListener('scroll', this.handleDesktopScroll, { passive: true });
                this.initDesktopNextButton();
                this.setActiveMediaIndex(0);
                this.updateDesktopNextButtonState();
                this.requestAsyncLoadImage();
            }

            // 绑定缩略图点击
            bindThumbClick(thumbsContentInner) {
                thumbsContentInner.removeEventListener('click', this.handleThumbClick);
                thumbsContentInner.addEventListener('click', this.handleThumbClick);
            }

            // 销毁Swiper实例
            destroySwiper() {
                if (!this.productSwipper) return;

                this.productSwipper.destroy(true, true);
                this.productSwipper = undefined;
            }

            // 销毁图片预览实例
            destroyViewer() {
                this.viewerRoot?.removeEventListener('click', this.handleViewerTrigger);
                this.viewerRoot = undefined;

                if (!this.viewerPreview) return;

                this.viewerPreview.destroy();
                this.viewerPreview = undefined;
            }

            // 销毁PC端滚动监听
            teardownDesktopMode() {
                if (window.innerWidth < 750) return;
                const mediaScroller = this.querySelector('.product-medias-swiper');
                const nextButton = this.querySelector('.swiper-button-next');

                if (!mediaScroller) return;

                mediaScroller.removeEventListener('scroll', this.handleDesktopScroll);
                if (nextButton) {
                    nextButton.removeEventListener('click', this.handleDesktopNextClick);
                    nextButton.remove();
                }
                this.clearThumbScrollLockTimer();
            }

            // 获取所有媒体项
            getSlides() {
                return Array.from(this.querySelectorAll('.product-swiper .swiper-slide'));
            }

            // 设置当前激活媒体
            setActiveMediaIndex(activeIndex, playVideo = true) {
                const slides = this.getSlides();
                const targetSlide = slides[activeIndex];

                if (!targetSlide || this.activeMediaIndex === activeIndex) {
                    this.updateActiveThumb(activeIndex);
                    return;
                }

                this.activeMediaIndex = activeIndex;
                this.updateActiveThumb(activeIndex);
                this.pauseAllVideos();

                if (playVideo) {
                    this.playSlideVideo(targetSlide);
                }
            }

            // 更新缩略图高亮
            updateActiveThumb(activeIndex) {
                const thumbsContentInner = this.querySelector('.thumbs-content_inner');
                const thumbItems = thumbsContentInner?.querySelectorAll('.thumb-item');

                if (!thumbItems?.length || activeIndex < 0) return;

                thumbItems.forEach(slide => {
                    slide.classList.remove('thumb-item-active');
                });

                thumbItems[activeIndex]?.classList.add('thumb-item-active');
                this.scrollToThumb(activeIndex);
            }

            // 处理缩略图点击
            handleThumbClick(e) {
                const thumbItem = e.target.closest('.thumb-item');
                const thumbIndex = Number(thumbItem?.getAttribute('data-thumb-index'));

                if (!thumbItem || Number.isNaN(thumbIndex)) return;

                if (window.innerWidth < 750 && this.productSwipper) {
                    this.productSwipper.slideToLoop(thumbIndex);
                    return;
                }

                this.setActiveMediaIndex(thumbIndex);
                this.lockThumbActiveState(thumbIndex);
                this.scrollToMedia(thumbIndex);
            }

            // 处理PC端滚动同步
            handleDesktopScroll() {
                const mediaScroller = this.querySelector('.product-medias-swiper');
                const slides = this.getSlides();

                if (!mediaScroller || !slides.length) return;

                this.requestAsyncLoadImage();

                if (this.thumbScrollLockIndex >= 0) {
                    this.updateActiveThumb(this.thumbScrollLockIndex);
                    this.updateDesktopNextButtonState();
                    this.scheduleThumbScrollUnlock();
                    return;
                }

                const currentCenter = mediaScroller.scrollTop + (mediaScroller.clientHeight / 2);
                let activeIndex = 0;
                let minDistance = Number.POSITIVE_INFINITY;

                slides.forEach((slide, index) => {
                    const slideCenter = slide.offsetTop + (slide.offsetHeight / 2);
                    const currentDistance = Math.abs(slideCenter - currentCenter);

                    if (currentDistance < minDistance) {
                        minDistance = currentDistance;
                        activeIndex = index;
                    }
                });

                this.setActiveMediaIndex(activeIndex);
                this.updateDesktopNextButtonState();
            }

            // 滚动到指定媒体
            scrollToMedia(thumbIndex) {
                const mediaScroller = this.querySelector('.product-medias-swiper');
                const thumbItem = this.getSlides()[thumbIndex];

                if (!mediaScroller || !thumbItem) return;

                thumbItem.querySelectorAll('[data-async-defer="true"]').forEach((image) => this.loadDeferredImage(image));

                mediaScroller.scrollTo({
                    top: thumbItem.offsetTop,
                    behavior: 'smooth'
                });

                this.updateDesktopNextButtonState(thumbIndex);
                this.requestAsyncLoadImage();
            }

            // 初始化PC端底部“下一张”按钮
            initDesktopNextButton() {
                const mediaScroller = this.querySelector('.product-medias-swiper');

                if (!mediaScroller) return;

                let nextButton = this.querySelector('.swiper-button-next');
                if (nextButton) {
                    nextButton.removeEventListener('click', this.handleDesktopNextClick);
                    nextButton.remove();
                }

                if (window.getComputedStyle(mediaScroller).position === 'static') {
                    mediaScroller.style.position = 'relative';
                }

                nextButton = document.createElement('button');
                nextButton.type = 'button';
                nextButton.className = 'swiper-button-next';
                nextButton.setAttribute('aria-label', '查看下一张媒体');
                nextButton.addEventListener('click', this.handleDesktopNextClick);
                mediaScroller.appendChild(nextButton);
            }

            // 点击PC端“下一张”按钮
            handleDesktopNextClick() {
                const slides = this.getSlides();

                if (!slides.length) return;

                const currentIndex = this.activeMediaIndex >= 0 ? this.activeMediaIndex : 0;
                const nextIndex = Math.min(currentIndex + 1, slides.length - 1);

                if (nextIndex === currentIndex) {
                    this.updateDesktopNextButtonState(nextIndex);
                    return;
                }

                this.setActiveMediaIndex(nextIndex);
                this.lockThumbActiveState(nextIndex);
                this.scrollToMedia(nextIndex);
            }

            // 根据当前媒体更新PC端“下一张”按钮显示状态
            updateDesktopNextButtonState(targetIndex) {
                if (window.innerWidth < 750) return;
                const nextButton = this.querySelector('.swiper-button-next');
                const slides = this.getSlides();
                const currentIndex = Number.isInteger(targetIndex)
                    ? targetIndex
                    : (this.activeMediaIndex >= 0 ? this.activeMediaIndex : 0);

                if (!nextButton || !slides.length || window.innerWidth < 750) return;

                const isLastSlide = currentIndex >= (slides.length - 1);
                nextButton.style.visibility = isLastSlide ? 'hidden' : 'visible';
                nextButton.style.pointerEvents = isLastSlide ? 'none' : 'auto';
            }

            // 触发容器内异步图片懒加载
            requestAsyncLoadImage() {
                if (typeof window.asyncLoadImage !== 'function') return;

                if (this.asyncImageFrame) {
                    window.cancelAnimationFrame(this.asyncImageFrame);
                }

                this.asyncImageFrame = window.requestAnimationFrame(() => {
                    this.asyncImageFrame = undefined;
                    window.asyncLoadImage();
                });
            }

            // Keep non-primary product images out of the page-wide image scan.
            // Each scrollable gallery area loads its own images shortly before use.
            initDeferredImageLoading() {
                const deferredImages = this.querySelectorAll('[data-async-defer="true"]');
                if (!deferredImages.length) return;

                if (!('IntersectionObserver' in window)) {
                    deferredImages.forEach((image) => this.loadDeferredImage(image));
                    return;
                }

                const observeImages = (root, images) => {
                    if (!root || !images.length) return;

                    const observer = new IntersectionObserver((entries) => {
                        entries.forEach((entry) => {
                            if (!entry.isIntersecting) return;
                            this.loadDeferredImage(entry.target);
                            observer.unobserve(entry.target);
                        });
                    }, { root, rootMargin: '200px 0px' });

                    images.forEach((image) => observer.observe(image));
                    this.deferredImageObservers.push(observer);
                };

                const thumbnails = Array.from(deferredImages).filter((image) => image.closest('.thumbs-container'));
                const galleryImages = Array.from(deferredImages).filter((image) => !image.closest('.thumbs-container'));

                observeImages(this.querySelector('.thumbs-container'), thumbnails);
                observeImages(this.querySelector('.product-medias-swiper'), galleryImages);
            }

            teardownDeferredImageLoading() {
                this.deferredImageObservers.forEach((observer) => observer.disconnect());
                this.deferredImageObservers = [];
            }

            loadDeferredImage(image) {
                if (!(image instanceof HTMLImageElement) || image.dataset.imgLoaded === 'true') return;

                const displayWidth = Math.min(
                    Number(image.dataset.rawWidth) || Number(image.dataset.designWidth) || 150,
                    Number(image.dataset.designWidth) || 150
                );
                const widthPrefix = `_${image.dataset.defaultWidth || '10x'}.`;
                const displaySrc = image.getAttribute('src')?.replace(widthPrefix, `_${displayWidth}x.`);
                if (!displaySrc) return;

                const preloadImage = new Image();
                preloadImage.src = displaySrc;
                preloadImage.onload = () => {
                    image.src = displaySrc;
                    image.dataset.imgLoaded = 'true';
                    image.removeAttribute('data-filename');
                    image.removeAttribute('data-async-defer');
                };
            }

            // 锁定点击时的缩略图高亮，避免平滑滚动过程中闪动
            lockThumbActiveState(thumbIndex) {
                this.thumbScrollLockIndex = thumbIndex;
                this.updateActiveThumb(thumbIndex);
                this.scheduleThumbScrollUnlock();
            }

            // 平滑滚动结束后恢复自动同步
            scheduleThumbScrollUnlock() {
                this.clearThumbScrollLockTimer();

                this.thumbScrollLockTimer = window.setTimeout(() => {
                    this.thumbScrollLockIndex = -1;
                }, 180);
            }

            // 清理缩略图高亮锁定定时器
            clearThumbScrollLockTimer() {
                if (!this.thumbScrollLockTimer) return;

                window.clearTimeout(this.thumbScrollLockTimer);
                this.thumbScrollLockTimer = undefined;
            }

            scrollToThumb(thumbIndex) {
                const thumbsContentInner = this.querySelector('.thumbs-content_inner');
                const thumbsContainer = this.querySelector('.thumbs-container');
                const thumbItem = thumbsContentInner.querySelectorAll('.thumb-item')[thumbIndex];

                if (!thumbItem || !thumbsContainer) return;

                // 滚动到中间，小于750px为横向滚动，大于750px为纵向滚动
                if (window.innerWidth < 750) {
                    thumbsContainer.scrollTo({
                        left: thumbItem.offsetLeft - (thumbsContainer.clientWidth / 2),
                        behavior: 'smooth'
                    });
                } else {
                    thumbsContainer.scrollTo({
                        top: thumbItem.offsetTop - (thumbsContainer.clientHeight / 2),
                        behavior: 'smooth'
                    });
                }
            }

            initViewer() {
                /**
                 * https://fengyuanchen.github.io/viewerjs/
                 */
                const viewerRoot = this.querySelector('.product-swiper');

                if (!viewerRoot) return;

                this.viewerRoot = viewerRoot;
                viewerRoot.addEventListener('click', this.handleViewerTrigger);
            }

            async handleViewerTrigger(event) {
                const image = event.target.closest('img');
                if (!image || !this.viewerRoot?.contains(image)) return;

                event.preventDefault();
                this.viewerRoot.removeEventListener('click', this.handleViewerTrigger);

                const imageIndex = Array.from(this.viewerRoot.querySelectorAll('img')).indexOf(image);

                try {
                    await this.loadViewerAssets();
                } catch (error) {
                    this.viewerAssetsPromise = undefined;
                    console.error('Unable to load the product image viewer.', error);
                    this.viewerRoot?.addEventListener('click', this.handleViewerTrigger);
                    return;
                }

                if (!this.viewerRoot || typeof Viewer !== 'function') return;

                this.viewerPreview = new Viewer(this.viewerRoot, {
                    zoomRatio: 0.5,
                    minZoomRatio: 0.1, // 最小缩放比例
                    maxZoomRatio: 10, // 最大缩放比例
                    initialCoverage: 0.95
                });

                window.requestAnimationFrame(() => {
                    if (!this.viewerPreview) return;
                    this.viewerPreview.view(Math.max(imageIndex, 0));
                    this.viewerPreview.show();
                });
            }

            loadViewerAssets() {
                if (typeof Viewer === 'function') return Promise.resolve();
                if (this.viewerAssetsPromise) return this.viewerAssetsPromise;

                const scriptUrl = this.dataset.viewerScriptUrl;
                const styleUrl = this.dataset.viewerStyleUrl;

                if (!scriptUrl || !styleUrl) {
                    return Promise.reject(new Error('Viewer asset URLs are missing.'));
                }

                const loadStyle = new Promise((resolve, reject) => {
                    const existingStyle = document.getElementById('DeferredViewerStyle');
                    if (existingStyle) {
                        if (existingStyle.dataset.loaded === 'true' || existingStyle.sheet) {
                            resolve();
                        } else {
                            existingStyle.addEventListener('load', resolve, { once: true });
                            existingStyle.addEventListener('error', reject, { once: true });
                        }
                        return;
                    }

                    const stylesheet = document.createElement('link');
                    stylesheet.id = 'DeferredViewerStyle';
                    stylesheet.rel = 'stylesheet';
                    stylesheet.href = styleUrl;
                    stylesheet.addEventListener('load', () => {
                        stylesheet.dataset.loaded = 'true';
                        resolve();
                    }, { once: true });
                    stylesheet.addEventListener('error', (error) => {
                        stylesheet.remove();
                        reject(error);
                    }, { once: true });
                    document.head.appendChild(stylesheet);
                });

                const loadScript = new Promise((resolve, reject) => {
                    const existingScript = document.getElementById('DeferredViewerScript');
                    if (existingScript) {
                        if (typeof Viewer === 'function') {
                            resolve();
                        } else {
                            existingScript.addEventListener('load', resolve, { once: true });
                            existingScript.addEventListener('error', reject, { once: true });
                        }
                        return;
                    }

                    const script = document.createElement('script');
                    script.id = 'DeferredViewerScript';
                    script.src = scriptUrl;
                    script.async = true;
                    script.addEventListener('load', resolve, { once: true });
                    script.addEventListener('error', (error) => {
                        script.remove();
                        reject(error);
                    }, { once: true });
                    document.head.appendChild(script);
                });

                this.viewerAssetsPromise = Promise.all([loadStyle, loadScript]);
                return this.viewerAssetsPromise;
            }

            // 颜色选项缩略图预览
            initColorVariantThumb() {
                if (window.innerWidth < 750) return;
                const colorVariantThumb = document.querySelectorAll('.swatch-label-image[data-color-variant-image');
                const id = 'swatch-label-image-preview';
                colorVariantThumb.forEach(item => {

                    // 鼠标进入
                    item.addEventListener('mouseenter', () => {
                        if (document.querySelector(`#${id}`)) {
                            document.querySelector(`#${id}`).remove();
                        }

                        const imgUrl = item.getAttribute('data-color-variant-image');
                        const thumbTemplate = `
                            <div id="${id}" class="top-0 left-0" style="position: absolute;left: ${0}px; top: ${0}px; transform: translate(-103%, 10%); z-index: 1000;border-radius: 8px;overflow: hidden;box-shadow: 0 4px 8px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.1);">
                                <img src="${imgUrl}" width="380" height="auto" alt="" srcset="">
                            </div>
                        `;

                        // 将thumbTemplate转为dom
                        const thumbTemplateDom = new DOMParser().parseFromString(thumbTemplate, 'text/html').body.firstChild;
                        item.appendChild(thumbTemplateDom);


                    });

                    // 鼠标离开
                    item.addEventListener('mouseleave', () => {
                        if (item.querySelector(`#${id}`)) {
                            item.querySelector(`#${id}`).remove();
                        }
                    });
                });
            }

            // 暂停所有视频播放
            pauseAllVideos() {
                const allSlides = this.querySelectorAll('.swiper-slide');
                allSlides.forEach(slide => {
                    // 暂停 HTML5 视频
                    const video = slide.querySelector('video');
                    if (video) {
                        video.pause();
                    }

                    // 暂停 YouTube/Vimeo 视频（通过 iframe）
                    const iframe = slide.querySelector('iframe');
                    if (iframe) {
                        const iframeSrc = iframe.src;
                        // YouTube
                        if (iframeSrc.includes('youtube.com') || iframeSrc.includes('youtu.be')) {
                            iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                        }
                        // Vimeo
                        if (iframeSrc.includes('vimeo.com')) {
                            iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
                        }
                    }
                });
            }

            // 懒加载并播放当前幻灯片中的视频
            playCurrentSlideVideo() {
                if (!this.productSwipper) return;

                const activeSlide = this.productSwipper?.slides?.[this.productSwipper.activeIndex];

                this.playSlideVideo(activeSlide);
            }

            // 播放指定媒体中的视频
            playSlideVideo(activeSlide) {
                if (!activeSlide) return;

                const mediaType = activeSlide.getAttribute('data-media-type');

                // 只对视频类型进行处理
                if (mediaType !== 'video' && mediaType !== 'external_video') return;

                const videoContainer = activeSlide.querySelector('.product-media-video[data-video-lazy]');

                if (!videoContainer) return;

                // 检查视频是否已加载
                const isLoaded = videoContainer.getAttribute('data-video-loaded') === 'true';

                if (!isLoaded) {
                    // 懒加载视频
                    this.lazyLoadVideo(videoContainer);
                    return;
                }

                // 视频已加载，直接播放
                const video = videoContainer.querySelector('video');
                if (video) {
                    video.play().catch(err => {
                        console.log('Video autoplay failed:', err);
                    });
                }

                // 处理 iframe 视频（YouTube/Vimeo）
                const iframe = videoContainer.querySelector('iframe');
                if (iframe) {
                    this.playIframeVideo(iframe);
                }
            }

            // 懒加载视频
            lazyLoadVideo(videoContainer) {
                const placeholder = videoContainer.querySelector('.video-placeholder');
                const videoType = videoContainer.getAttribute('data-video-type');

                // 移除占位符
                if (placeholder) {
                    placeholder.remove();
                }

                if (videoType === 'video') {
                    // Shopify 托管视频：创建 video 元素
                    this.createNativeVideo(videoContainer);
                } else if (videoType === 'external_video') {
                    // 外部视频：创建 iframe
                    this.createExternalVideo(videoContainer);
                }

                // 标记为已加载
                videoContainer.setAttribute('data-video-loaded', 'true');
            }

            // 创建原生视频元素
            createNativeVideo(container) {
                const isMobile = window.innerWidth < 750;
                const video = document.createElement('video');
                video.setAttribute('controls', '');
                video.setAttribute('playsinline', '');
                video.style.width = '100%';
                video.style.height = isMobile ? '100%' : 'auto';
                video.style.objectFit = 'contain';

                // 获取所有视频源
                const attributes = container.attributes;
                const sources = [];

                for (let i = 0; i < attributes.length; i++) {
                    const attr = attributes[i];
                    if (attr.name.startsWith('data-video-src-')) {
                        const format = attr.name.replace('data-video-src-', '');
                        const mimeType = container.getAttribute(`data-video-mime-${format}`);
                        sources.push({
                            src: attr.value,
                            type: mimeType
                        });
                    }
                }

                // 添加视频源
                sources.forEach(sourceData => {
                    const source = document.createElement('source');
                    source.src = sourceData.src;
                    source.type = sourceData.type;
                    video.appendChild(source);
                });

                // 添加到容器
                container.appendChild(video);

                // 尝试播放
                setTimeout(() => {
                    video.play().catch(err => {
                        console.log('Video autoplay failed:', err);
                    });
                }, 100);
            }

            // 创建外部视频 iframe
            createExternalVideo(container) {
                const isMobile = window.innerWidth < 750;
                const host = container.getAttribute('data-video-host');
                const externalId = container.getAttribute('data-video-external-id');

                if (!host || !externalId) return;

                const iframe = document.createElement('iframe');
                iframe.setAttribute('frameborder', '0');
                iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
                iframe.setAttribute('allowfullscreen', '');
                iframe.style.width = '100%';
                iframe.style.height = isMobile ? '100%' : 'auto';
                iframe.style.aspectRatio = '16 / 9';

                // 根据视频平台设置 URL
                if (host === 'youtube') {
                    iframe.src = `https://www.youtube.com/embed/${externalId}?enablejsapi=1&autoplay=1`;
                } else if (host === 'vimeo') {
                    iframe.src = `https://player.vimeo.com/video/${externalId}?autoplay=1`;
                }

                // 添加到容器
                container.appendChild(iframe);
            }

            // 播放 iframe 视频（YouTube/Vimeo）
            playIframeVideo(iframe) {
                if (!iframe) return;

                const iframeSrc = iframe.src;

                // YouTube
                if (iframeSrc.includes('youtube.com') || iframeSrc.includes('youtu.be')) {
                    // 确保 iframe URL 包含 enablejsapi 参数
                    if (!iframeSrc.includes('enablejsapi=1')) {
                        const separator = iframeSrc.includes('?') ? '&' : '?';
                        iframe.src = iframeSrc + separator + 'enablejsapi=1';
                    }

                    iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                }

                // Vimeo
                if (iframeSrc.includes('vimeo.com')) {
                    iframe.contentWindow?.postMessage('{"method":"play"}', '*');
                }
            }
        }
    );

}
